import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { MonitorAndProtectModelCampaignsCampaignModel } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { MNP_ROUTES, mnpNavigation } from "app/pages/digital-marketing/monitor-and-protect/app/navigation";
import {
    useMonitorAndProtectCampaigns,
    useMonitorAndProtectCurrentCampaign,
} from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { homepageTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/homepage";
import { CreateCampaignWizard } from "app/pages/digital-marketing/monitor-and-protect/shared/components/create-campaign-wizard";
import { DomainSearch } from "app/pages/digital-marketing/monitor-and-protect/shared/components/forms/domain-search";
import { usePreloadedHook } from "app/pages/digital-marketing/monitor-and-protect/shared/hooks/preloaded-hook";
import { DemoIcon } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/icons/demo";
import { isNoTouchOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

const ROUTES_WITHOUT_DEMO_BANNER = [MNP_ROUTES.home, MNP_ROUTES.settings];

export const DemoBanner = () => {
    const translate = useTranslation();
    const { state } = useCurrentStateAndParams();
    const { data: current } = useMonitorAndProtectCurrentCampaign();
    const { isReady, openHook } = usePreloadedHook();
    const { data: campaigns } = useMonitorAndProtectCampaigns({ includeDemoCampaigns: false });
    const [selectedDomain, setSelectedDomain] = useState("");
    const [isWizardOpen, setIsWizardOpen] = useState(false);

    const isDemo = current?.type === "Demo";

    const isCampaignsLimitReached = () => {
        const campaignsLimit = swSettings.components.MonitorAndProtectMaster.resources.MaximumAccountCampaigns;
        if (campaignsLimit === -1) return false;
        return (campaigns?.length ?? 0) >= campaignsLimit;
    };

    const onDomainSelect = (domain: string) => {
        setSelectedDomain(domain);
    };

    const handleCreateCampaignClick = () => {
        if (isReady && swSettings.components.MonitorAndProtectMaster.resources.MaximumAccountCrawls === 0) {
            if (isNoTouchOrAfterTrial(swSettings)) {
                openHook("monitorAndProtectNoTouch", "v6");
            } else {
                openHook("MonitorAndProtectUnlock", "v5");
            }
            return;
        }

        if (isReady && isCampaignsLimitReached()) {
            openHook("MonitorAndProtectUpgrade");
            return;
        }

        if (selectedDomain) {
            setIsWizardOpen(true);
        }
    };

    const handleWizardClose = () => {
        setIsWizardOpen(false);
    };

    const handleCampaignCreated = (campaign: MonitorAndProtectModelCampaignsCampaignModel) => {
        void mnpNavigation.overview.go({ campaignId: campaign.id, ref: "wizard" });
    };

    if (ROUTES_WITHOUT_DEMO_BANNER.includes(state.name) || !isDemo) {
        return null;
    }

    return (
        <>
            <CreateCampaignWizard
                key={selectedDomain}
                isOpen={isWizardOpen}
                selectedDomain={selectedDomain}
                onClose={handleWizardClose}
                onCampaignCreated={handleCampaignCreated}
            />
            <Flex
                background={colorsPalettes.indigo[100]}
                padding="16px"
                borderRadius="8px"
                alignItems="center"
                justifyContent="space-between"
                gap="16px"
                margin="0 0 24px 0"
            >
                <Flex gap="20px">
                    <DemoIcon />

                    <Stack gap="4px">
                        <Text
                            fontVariant="h6"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("monitor_and_protect.shared.demo_banner.title")}
                        </Text>
                        <Text
                            fontVariant="input2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("monitor_and_protect.shared.demo_banner.subtitle")}
                        </Text>
                    </Stack>
                </Flex>
                <Flex
                    alignItems="center"
                    gap="16px"
                    width="50%"
                    justifyContent="flex-end"
                >
                    <DomainSearch
                        placeholder={translate("monitor_and_protect.shared.demo_banner.domain_placeholder")}
                        value={selectedDomain}
                        onSelect={onDomainSelect}
                        onSearchBarClick={homepageTracking.onSearchBarClick}
                        onSearchResultClick={homepageTracking.onSearchResultClick}
                    />
                    <Button
                        style={{ whiteSpace: "nowrap", minWidth: "max-content" }}
                        onClick={handleCreateCampaignClick}
                        isDisabled={!selectedDomain}
                    >
                        {translate("monitor_and_protect.shared.demo_banner.create_campaign_button")}
                    </Button>
                </Flex>
            </Flex>
        </>
    );
};
